import { useState, useRef, useEffect } from 'react';
import { Folder, FolderOpen, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { Card, cn } from '@ai-tutor/ui';
import type { FolderItem } from '@/lib/api-client';

interface FolderCardProps {
  folder: FolderItem;
  onOpen: (folder: FolderItem) => void;
  onRename: (folder: FolderItem) => void;
  onDelete: (folder: FolderItem) => void;
}

export function FolderCard({
  folder,
  onOpen,
  onRename,
  onDelete,
}: FolderCardProps) {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  const itemCount = folder.lesson_count + folder.subfolder_count;
  const countLabel =
    itemCount === 0
      ? 'Empty'
      : itemCount === 1
        ? '1 item'
        : `${itemCount} items`;

  return (
    <Card
      className='group relative flex h-full cursor-pointer flex-col overflow-hidden transition-all duration-300 sm:hover:scale-[1.02]'
      onClick={() => onOpen(folder)}
    >
      {/* Three-dot menu */}
      {/* Vertically centred on the phone row, top-right on the card. */}
      <div
        className='absolute right-2 top-1/2 z-10 -translate-y-1/2 sm:right-4 sm:top-4 sm:translate-y-0'
        ref={menuRef}
      >
        <button
          onClick={e => {
            e.stopPropagation();
            setShowMenu(v => !v);
          }}
          className='tap-target rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted'
          aria-label='Folder options'
        >
          <MoreVertical className='h-4 w-4' />
        </button>
        {showMenu && (
          <div className='absolute right-0 top-full z-20 mt-1 w-48 rounded-lg border border-border bg-card py-1 shadow-lg'>
            <button
              onClick={e => {
                e.stopPropagation();
                setShowMenu(false);
                onRename(folder);
              }}
              className='flex w-full items-center gap-2 px-4 py-3 text-left text-sm text-foreground transition-colors hover:bg-muted sm:py-2'
            >
              <Pencil className='h-4 w-4' />
              Rename
            </button>
            <button
              onClick={e => {
                e.stopPropagation();
                setShowMenu(false);
                onDelete(folder);
              }}
              className='flex w-full items-center gap-2 px-4 py-3 text-left text-sm text-destructive-emphasis transition-colors hover:bg-red-50 dark:hover:bg-red-900/20 sm:py-2'
            >
              <Trash2 className='h-4 w-4' />
              Delete Folder
            </button>
          </div>
        )}
      </div>

      {/* A one-line row on a phone (icon, name, count), the tall card from
          `sm`. Tags are dropped from the row: search matches them anyway. */}
      <div className='flex flex-1 flex-row items-center gap-3 p-3 pr-12 sm:flex-col sm:items-stretch sm:gap-0 sm:p-6'>
        <div
          className={cn(
            'relative inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg sm:mb-4 sm:h-12 sm:w-12',
            'bg-brand-100 text-brand-600 dark:bg-muted dark:text-primary'
          )}
        >
          <Folder className='h-6 w-6 transition-opacity duration-200 group-hover:opacity-0' />
          <FolderOpen className='absolute h-6 w-6 opacity-0 transition-opacity duration-200 group-hover:opacity-100' />
        </div>

        <div className='min-w-0'>
          <h2 className='line-clamp-1 text-base font-semibold leading-snug text-foreground sm:mb-2 sm:pr-8 sm:text-xl'>
            {folder.name}
          </h2>

          <p className='text-sm text-muted-foreground sm:mb-4'>{countLabel}</p>
        </div>

        <div className='mt-auto hidden sm:block'>
          {folder.tags.length > 0 && (
            <div className='flex flex-wrap gap-1.5'>
              {folder.tags.slice(0, 3).map(tag => (
                <span
                  key={tag}
                  className='inline-flex items-center rounded-md bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground'
                >
                  {tag}
                </span>
              ))}
              {folder.tags.length > 3 && (
                <span className='inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium text-muted-foreground'>
                  +{folder.tags.length - 3}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
